const getCapacitor = () => (typeof window !== 'undefined' ? window.Capacitor : null);
const getPlugin = () => getCapacitor()?.Plugins?.FLCNativeBilling || null;
const FINISH_QUEUE_KEY = 'flc.mobileBilling.pendingFinish.v1';

const getStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch (_) { return null; }
};

const readFinishQueue = () => {
  const storage = getStorage();
  if (!storage) return [];
  try {
    const value = JSON.parse(storage.getItem(FINISH_QUEUE_KEY) || '[]');
    return Array.isArray(value) ? value.filter(Boolean) : [];
  } catch (_) {
    return [];
  }
};

const writeFinishQueue = (items) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    if (!items.length) storage.removeItem(FINISH_QUEUE_KEY);
    else storage.setItem(FINISH_QUEUE_KEY, JSON.stringify(items));
  } catch (_) {}
};

const finishId = (proof = {}) => [
  proof.platform || '',
  proof.transactionId || proof.purchaseToken || '',
  proof.productId || '',
].join(':');

const buildFinishItem = (proof, purchase) => ({
  id: finishId(proof),
  proof: {
    platform: proof?.platform || '',
    transactionId: proof?.transactionId || '',
    purchaseToken: proof?.purchaseToken || '',
    productId: proof?.productId || '',
    acknowledged: proof?.acknowledged === true,
  },
  purchase: { purchaseProductType: purchase?.purchaseProductType || 'product' },
});

const enqueueFinish = (proof, purchase) => {
  const item = buildFinishItem(proof, purchase);
  if (!item.id || item.id === '::') return item;
  const queue = readFinishQueue().filter(existing => existing?.id !== item.id);
  queue.push(item);
  writeFinishQueue(queue);
  return item;
};

const removeFinish = (id) => writeFinishQueue(readFinishQueue().filter(item => item?.id !== id));

export const getNativeStorePlatform = () => {
  const capacitor = getCapacitor();
  const platform = typeof capacitor?.getPlatform === 'function' ? capacitor.getPlatform() : '';
  if (platform === 'ios') return 'apple';
  if (platform === 'android') return 'google';
  return null;
};


export const restoreNativePurchases = async () => {
  const plugin = getPlugin();
  const platform = getNativeStorePlatform();
  if (!plugin || platform !== 'google' || typeof plugin.restore !== 'function') {
    return [];
  }
  const result = await plugin.restore();
  return Array.isArray(result?.purchases) ? result.purchases.filter(Boolean) : [];
};

export const purchaseNative = async (purchase) => {
  const plugin = getPlugin();
  const platform = getNativeStorePlatform();
  if (!plugin || !platform) {
    const target = platform === 'apple' ? 'iOS' : platform === 'google' ? 'Android' : 'iOS or Android';
    const error = new Error(`This purchase must be completed in the ${target} app.`);
    error.code = 'NATIVE_BILLING_UNAVAILABLE';
    throw error;
  }
  const productId = platform === 'apple' ? purchase?.appleProductId : purchase?.googleProductId;
  if (!productId) {
    const storeName = platform === 'apple' ? 'App Store' : 'Google Play';
    const error = new Error(`This purchase is not available in ${storeName} yet.`);
    error.code = 'STORE_PRODUCT_NOT_CONFIGURED';
    error.storePlatform = platform;
    throw error;
  }
  const type = String(purchase?.purchaseProductType || 'product');
  const productType = type === 'subscription' || type === 'org_subscription' ? 'subs' : 'inapp';
  const result = await plugin.purchase({ productId, productType });
  return { ...result, platform, productId };
};

const finishNativePurchaseNow = async (proof, purchase) => {
  const plugin = getPlugin();
  if (!plugin || !proof) {
    const error = new Error('Native billing plugin is unavailable while finalizing the store purchase.');
    error.code = 'NATIVE_BILLING_UNAVAILABLE';
    throw error;
  }
  const type = String(purchase?.purchaseProductType || 'product');
  return plugin.finish({
    ...proof,
    productType: type === 'subscription' || type === 'org_subscription' ? 'subs' : 'inapp',
    consumable: type === 'custom_content',
  });
};

// Call this only after the backend has granted the entitlement. The finish request is
// persisted first so a transient StoreKit/Play Billing failure can be retried after
// navigation, app restart, or a temporary store-service disconnect.
export const finishNativePurchase = async (proof, purchase) => {
  if (!proof) return null;
  const pending = enqueueFinish(proof, purchase);
  const result = await finishNativePurchaseNow(proof, purchase);
  removeFinish(pending.id);
  return result;
};

export const retryPendingNativePurchaseFinishes = async () => {
  const queue = readFinishQueue();
  if (!queue.length) return { attempted: 0, finished: 0, remaining: 0 };
  let finished = 0;
  for (const item of queue) {
    try {
      await finishNativePurchaseNow(item.proof, item.purchase);
      removeFinish(item.id);
      finished += 1;
    } catch (_) {
      // Keep the item for the next foreground/play session.
    }
  }
  return { attempted: queue.length, finished, remaining: readFinishQueue().length };
};
